import { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("eme_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => localStorage.removeItem("eme_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    // MODO MONGO (emergencia Feb-2026): el login legacy Mongo es la fuente
    // de verdad para auth mientras Supabase está siendo migrado. Intentamos
    // Mongo primero porque emite un JWT cuyo `sub` matchea `users.id` y
    // todas las ~22 rutas de la app lo entienden.
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("eme_token", data.token);
      // Limpieza defensiva: borra tokens Supabase viejos que pudieron
      // quedar guardados en sesiones anteriores y hacen conflicto.
      localStorage.removeItem("sb_access_token");
      localStorage.removeItem("sb_refresh_token");
      setUser(data.user);
      return data.user;
    } catch (err) {
      const status = err?.response?.status;
      // Solo si el login Mongo da 404 (endpoint no existe aún) o 403
      // (usuario bloqueado) seguimos. Si da 401 (clave mala) rebotamos ya.
      if (status === 401) throw err;
      if (status && status !== 404) throw err;
    }
    // Fallback a Supabase (sólo si el legacy Mongo no existe por alguna razón)
    const { data } = await api.post("/auth/supabase/login", {
      email,
      password,
    });
    if (data.needs_password_reset) {
      return {
        __needs_password_reset: true,
        reset_token: data.reset_token,
        email: data.email,
        full_name: data.full_name,
        role: data.role,
      };
    }
    if (data.access_token) {
      localStorage.setItem("sb_access_token", data.access_token);
      localStorage.setItem("sb_refresh_token", data.refresh_token || "");
      if (data.token) localStorage.setItem("eme_token", data.token);
      setUser(data.profile);
      return data.profile;
    }
    throw new Error("Login falló por motivo desconocido");
  };

  const completeFirstLogin = async (reset_token, new_password) => {
    const { data } = await api.post("/auth/supabase/set-password", {
      reset_token,
      new_password,
    });
    localStorage.setItem("sb_access_token", data.access_token);
    localStorage.setItem("sb_refresh_token", data.refresh_token || "");
    if (data.token) localStorage.setItem("eme_token", data.token);
    setUser(data.profile);
    return data.profile;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("eme_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("eme_token");
    localStorage.removeItem("sb_access_token");
    localStorage.removeItem("sb_refresh_token");
    setUser(null);
  };

  const refresh = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, completeFirstLogin, register, logout, setUser, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
